import { type Draft } from 'immer';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import {
  applyPropsPatch,
  type CanvasElementId,
  type ColumnId,
  deleteWrapperRefsMutably,
  FIELD_INFO_TYPE_COMMIT,
  getFormGroupLikeWrapper,
  getFormGroupLikeWrapperMainFormId,
  getWizardSummaryFormsIds,
  type PropsOf,
  setWrapperRefMutably,
  WrapperForm,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementDataProperties,
  type DataBindingSourceInfo,
  FORM_GROUP_ACTION_MAPPING_MAIN,
} from '@blaze-shared/elements-domain';
import { type StyleSheet } from '@blaze-shared/theming-domain';

import { sanitizeWrapperRefsMutably } from '../../../+state/elements/elements.reducer.utils';
import { sanitizeConnectionsAndDataAfterUiChangeMutably } from '../../../+state/runtime/runtime.reducer.utils';
import { type CanvasElementDefinitionInternal } from '../../../canvas-elements/element-definition';
import {
  isElementType,
  isFormGroupLike,
  isVariantOfFormContainer,
} from '../../../canvas-elements/element-model/element-type-guards';
import { getOrCreateWrapperRefsMutably } from '../../../canvas-elements/element-model/wrapper-refs';
import { getPathBeforeElement, getPathLastInSlot } from '../../../utils/layout/helpers';
import { placeElementMutably } from '../../../utils/layout/place-element';
import { removeElementMutably } from '../../../utils/layout/remove-element';

/** Everything `applyFormGroupDataSourceMutably` needs to point a form-group-like at a dataset. */
export interface FormGroupDataSourceData {
  /** The form-group-like wrapper — a `formGroup` or a `wizard`. */
  elementId: CanvasElementId;
  /** The wrapper slot the main form (and, for form groups, the commit button) belongs in. */
  slotId: ColumnId;
  dataSource: DataBindingSourceInfo;
  styleSheets: StyleSheet[];
  formContainerDefinition: CanvasElementDefinitionInternal;
  buttonDefinition: CanvasElementDefinitionInternal;
}

/**
 * Points a form-group-like element at `dataSource`: places or rebinds its main form, re-keys the
 * wrapper refs onto the new dataset, and drops whatever the previous dataset left behind.
 *
 * Extracted from the `updateFormGroupDataSource` reducer so callers that already hold a draft —
 * generation services running a whole regeneration inside one `produce` — can reach the logic
 * without dispatching an action and waiting for the store to come back.
 *
 * @returns the id of the main form, whether it was just placed or already existed.
 */
export function applyFormGroupDataSourceMutably(
  draft: Draft<EngineStateBase>,
  {
    elementId,
    slotId,
    dataSource,
    styleSheets,
    formContainerDefinition,
    buttonDefinition,
  }: FormGroupDataSourceData,
): { formId: CanvasElementId } {
  const element = draft.entities[elementId];
  if (!isFormGroupLike(element)) {
    throw new Error('element is not form group like');
  }

  const dataSourceValue: CanvasElementDataProperties = {
    key: 'value',
    dataSource: {
      ...dataSource,
      sourceType: 'formGroup',
    },
    enabled: true,
  };
  const refs = getOrCreateWrapperRefsMutably(element);
  const { datasetId: oldDatasetId, mainFormId: existingFormId } =
    getFormGroupLikeWrapperMainFormId(refs);
  const newDatasetId = dataSource.id;

  if (oldDatasetId) {
    deleteWrapperRefsMutably(refs, WrapperForm.getRefIds(undefined, oldDatasetId));
  }

  let formId: CanvasElementId;

  if (!existingFormId) {
    const formTargetPath = refs[FIELD_INFO_TYPE_COMMIT]
      ? getPathBeforeElement(draft, refs[FIELD_INFO_TYPE_COMMIT])
      : getPathLastInSlot(draft, elementId, slotId);
    const newForm = placeElementMutably(draft, {
      elementDefinition: {
        ...formContainerDefinition,
        height: { mode: 'auto' },
        width: { mode: 'full' },
        ...(element.props.content.action &&
        FORM_GROUP_ACTION_MAPPING_MAIN[element.props.content.action] === 'view'
          ? {
              type: 'formDetails',
            }
          : {}),
      },
      atPath: formTargetPath,
      initProps: {
        data: [dataSourceValue],
        content: {
          action:
            element.props.content.action &&
            FORM_GROUP_ACTION_MAPPING_MAIN[element.props.content.action],
          origin: isElementType(element, 'formGroup')
            ? 'form-group'
            : isElementType(element, 'wizard')
              ? 'wizard'
              : undefined,
          title: { enabled: true, label: dataSource.name },
          options: { markRequiredFields: true },
        },
      },
      style: { styleSheets, useDefault: true },
    });
    if (!newForm?.elementId) {
      throw new Error('unable to place element');
    }
    const newFormElement = draft.entities[newForm.elementId];
    if (!isVariantOfFormContainer(newFormElement)) {
      throw new Error('unexpected form element type');
    }
    // The generated form spacing is intentional and must survive the style-path
    // cleanup performed by the default-style assignment
    newFormElement.props = applyPropsPatch(newFormElement.props, {
      style: { container: { offset: { padding: '$t.spacingFormGroupForm' } } },
    }) as PropsOf<'formContainer' | 'formDetails'>;
    formId = newForm.elementId;
    setWrapperRefMutably(refs, WrapperForm.getRefIds(undefined, newDatasetId), newForm.elementId);
    if (isElementType(element, 'wizard')) {
      const slotIndex = element.slots.ids.indexOf(slotId);
      if (slotIndex < 0) {
        throw new Error('invalid slotId');
      }
      if (!element.props.content.steps) {
        throw new Error('steps configuration missing');
      }
      element.props.content.steps.items[slotIndex].required = true;
    }
  } else {
    formId = existingFormId;
    setWrapperRefMutably(refs, WrapperForm.getRefIds(undefined, newDatasetId), formId);
    const form = draft.entities[formId];
    if (!isElementType(form, 'formContainer')) {
      return { formId };
    }
    form.props.data = [
      ...(form.props.data ?? []).filter(({ key }) => key !== 'value'),
      dataSourceValue,
    ];
    if (!dataSource.name) {
      throw new Error('main form has unnamed data source');
    }
    if (form.props.content.title) {
      form.props.content.title.label = dataSource.name;
    }
  }

  if (isElementType(element, 'formGroup') && !refs[FIELD_INFO_TYPE_COMMIT]) {
    const initProps = applyPropsPatch(buttonDefinition.initial, {
      content: {
        button: { label: 'Save' },
      },
    });
    const newButton = placeElementMutably(draft, {
      elementDefinition: buttonDefinition,
      atPath: getPathLastInSlot(draft, elementId, slotId),
      initProps,
      style: { styleSheets, useDefault: true },
    });
    if (!newButton?.elementId) {
      throw new Error('unable to place element');
    }
    refs[FIELD_INFO_TYPE_COMMIT] = newButton.elementId;
  }

  // why: keyed on the form that was already there, not on `formId` — when the main form is placed
  // fresh there is nothing stale to drop, so this pass is a no-op by design.
  getFormGroupLikeWrapper(refs).forEach((entry) => {
    if (!existingFormId || entry.formId === existingFormId) {
      return;
    }
    removeElementMutably(draft, entry.formId);
  });
  if (isElementType(element, 'wizard')) {
    getWizardSummaryFormsIds(refs).forEach(({ detailsId }) => {
      removeElementMutably(draft, detailsId);
    });
    element.props.content.steps?.items
      .filter(({ type }) => type === 'review')
      .forEach((step) => {
        step.reviewForms = [];
      });
  }
  sanitizeWrapperRefsMutably(draft, elementId);
  sanitizeConnectionsAndDataAfterUiChangeMutably(draft);

  return { formId };
}
